/**
 * Bringing a file in — `docs/catalog-and-packages.md` §7.
 *
 * Four steps, because an import that goes straight from a file to "1,240 rows
 * applied" is an import nobody dares press. **Bring it in** takes the file or
 * a paste; **Match columns** shows what we think each column is and lets
 * somebody disagree; **Clean up** says what looks wrong before it is written;
 * **Import** applies it and offers to put it back.
 *
 * The dialog it replaces read the file, matched headers by exact label, and
 * showed four counts. That works for a file exported from Rowted and for
 * nothing else: a supplier's price list says "Item Code" and "Unit Cost", and
 * an exact-label match drops both and imports a column of names.
 *
 * **Only .csv, and pasting.** `.xlsx` needs a spreadsheet parser and the one
 * on npm (`xlsx@0.18.5`) carries unfixed prototype-pollution and ReDoS
 * advisories against exactly this input — an untrusted workbook. The fix is
 * only in SheetJS's own CDN build, which is a non-registry dependency. That is
 * a call to make deliberately rather than inside an import screen, so it is
 * not made here; the step says so rather than failing at the file picker.
 */
import { useState } from 'react'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { Button, MetricCard } from './primitives.js'
import { Modal } from './overlays.js'

export interface PreviewRow {
  rowNumber: number
  action: 'create' | 'update' | 'skip' | 'error'
  matchReason: string | null
  payload: Record<string, string>
  error: string | null
}

interface ProposedColumn {
  column: string
  field: string | null
  confidence: number
  sample: string | null
  source: 'label' | 'alias' | 'model' | 'none'
}

interface Preview {
  batchId: string
  rows: PreviewRow[]
  counts: Record<string, number>
}

type Step = 'file' | 'match' | 'clean' | 'done'

export function SmartImport({
  area, orgUnitId, fields, onClose, onDone,
}: {
  area: string
  orgUnitId: string
  /** What this area can be given, for the target selects. */
  fields: { name: string; label: string }[]
  onClose: () => void
  onDone: (summary: string) => void
}) {
  const [step, setStep] = useState<Step>('file')
  const [filename, setFilename] = useState('pasted.csv')
  const [csv, setCsv] = useState('')
  const [pasted, setPasted] = useState('')
  const [columns, setColumns] = useState<ProposedColumn[]>([])
  /*
   * What the select may offer — from the server, because only the server
   * knows which fields an import can write. `items.unitCost` is a division in
   * SQL with no column behind it, and offering it is offering nothing.
   */
  const [targets, setTargets] = useState<{ name: string; label: string }[]>([])
  const [preview, setPreview] = useState<Preview | null>(null)
  const [summary, setSummary] = useState<{ batchId: string; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Header row and the first few rows, for the mapping call. */
  async function readColumns(text: string, name: string) {
    setBusy(true)
    setError(null)
    try {
      const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '')
      if (lines.length < 2) throw new Error('That file has a header and no rows.')
      const headers = splitRow(lines[0]!)
      const sample = lines.slice(1, 6).map((line) => {
        const cells = splitRow(line)
        return Object.fromEntries(headers.map((header, index) => [header, cells[index] ?? '']))
      })

      const body = await request<{
        mapping: ProposedColumn[]; targets: { name: string; label: string }[]
      }>(`/${area}/import/mapping`, { method: 'POST', body: { headers, sample } })

      setCsv(text)
      setFilename(name)
      setColumns(body.mapping)
      setTargets(body.targets)
      setStep('match')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That file could not be read.')
    } finally {
      setBusy(false)
    }
  }

  async function runPreview() {
    setBusy(true)
    setError(null)
    try {
      const mapping = Object.fromEntries(columns.map((row) => [row.column, row.field]))
      setPreview(await request<Preview>(
        `/${area}/import/preview`, { method: 'POST', body: { filename, csv, mapping } }))
      setStep('clean')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be previewed.')
    } finally {
      setBusy(false)
    }
  }

  async function apply() {
    if (!preview) return
    setBusy(true)
    try {
      const result = await request<{ created: number; updated: number; skipped: number }>(
        `/${area}/import/apply`,
        { method: 'POST', body: { batchId: preview.batchId, orgUnitId } })
      setSummary({
        batchId: preview.batchId,
        text: `${result.created} added, ${result.updated} updated, ${result.skipped} skipped.`,
      })
      setStep('done')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The import could not be applied.')
    } finally {
      setBusy(false)
    }
  }

  async function undo() {
    if (!summary) return
    setBusy(true)
    try {
      const result = await request<{ restored: number; deactivated: number }>(
        `/${area}/import/undo`, { method: 'POST', body: { batchId: summary.batchId } })
      onDone(`Put back: ${result.restored} restored, ${result.deactivated} switched off.`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be undone.')
      setBusy(false)
    }
  }

  async function downloadTemplate() {
    const body = await request<{ csv: string }>(
      // A template has no view: there are no rows to filter or sort.
      `/${area}/export`, { method: 'POST', body: { template: true } })
    const url = URL.createObjectURL(new Blob([body.csv], { type: 'text/csv' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `${area.replace(/\//g, '-')}-template.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const mapped = columns.filter((row) => row.field !== null).length
  const problems = preview?.rows.filter((row) => row.action === 'error') ?? []
  const willWrite = (preview?.counts.create ?? 0) + (preview?.counts.update ?? 0)

  return (
    <Modal
      open
      onClose={onClose}
      title={{
        file: 'Bring it in',
        match: 'Match columns',
        clean: 'Clean up',
        done: 'Imported',
      }[step]}
      footer={(
        <>
          {step === 'match' && <Button onClick={() => setStep('file')}>Back</Button>}
          {step === 'clean' && <Button onClick={() => setStep('match')}>Back</Button>}
          {step !== 'done' && <Button onClick={onClose}>Cancel</Button>}

          {step === 'file' && (
            <Button
              variant="primary"
              disabled={busy || pasted.trim() === ''}
              onClick={() => void readColumns(pasted, 'pasted.csv')}
            >
              Use what I pasted
            </Button>
          )}
          {step === 'match' && (
            <Button variant="primary" disabled={busy || mapped === 0} onClick={() => void runPreview()}>
              {busy ? 'Looking…' : 'Check it over'}
            </Button>
          )}
          {step === 'clean' && (
            <Button variant="primary" disabled={busy || willWrite === 0} onClick={() => void apply()}>
              {busy ? 'Working…' : `Import ${willWrite} rows`}
            </Button>
          )}
          {step === 'done' && (
            <>
              <Button disabled={busy} onClick={() => void undo()}>Undo this import</Button>
              <Button variant="primary" onClick={() => onDone(summary?.text ?? 'Imported.')}>
                Done
              </Button>
            </>
          )}
        </>
      )}
    >
      {step === 'file' && (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-cell text-text-muted">
            A row with an <code>id</code> updates that record; a row without one is added.
            Nothing is ever deleted by an import, and you can put the whole batch back
            for a week afterwards.
          </p>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-text-faint">
              A CSV file
            </span>
            <input
              type="file"
              accept=".csv,text/csv"
              disabled={busy}
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) void file.text().then((text) => readColumns(text, file.name))
              }}
              className="text-cell text-text"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-text-faint">
              Or paste it
            </span>
            <textarea
              value={pasted}
              onChange={(event) => setPasted(event.target.value)}
              rows={5}
              placeholder="Name,Unit,Cost&#10;Shredded hardwood,cu yd,31.50"
              className={cn('w-full rounded-field border border-line bg-surface p-2',
                'font-mono text-[12px] text-text',
                'focus:border-accent-line focus:outline-none focus:ring-2 focus:ring-accent-ring')}
            />
          </label>

          <p className="m-0 flex flex-wrap items-center gap-2 text-[12px] text-text-muted">
            <button
              type="button"
              onClick={() => void downloadTemplate()}
              className="font-semibold text-accent hover:text-accent-dark"
            >
              Download a template
            </button>
            · A spreadsheet works if you save it as CSV first — .xlsx is not read here yet.
          </p>
        </div>
      )}

      {step === 'match' && (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-cell text-text-muted">
            {mapped} of {columns.length} columns matched. Change anything that is wrong;
            set a column to <b>Ignore</b> and it is not read.
          </p>

          <div className="max-h-[320px] overflow-y-auto">
            {columns.map((row, index) => (
              <div
                key={row.column}
                className={cn('grid grid-cols-[minmax(0,1fr)_120px_minmax(0,1fr)] items-center',
                  'gap-2 py-1.5',
                  index < columns.length - 1 && 'border-b border-line-soft')}
              >
                <span className="flex min-w-0 flex-col">
                  <b className="truncate text-cell font-semibold text-text">{row.column}</b>
                  <span className="truncate text-[11px] text-text-faint">
                    {row.sample ?? 'empty'}
                  </span>
                </span>

                {/*
                  * How sure we are, as a dot. A guess from the model is the
                  * one somebody should actually look at, which is why it is
                  * amber rather than green however confident it claims to be.
                  */}
                <span className="flex items-center gap-1.5 text-[11px] text-text-muted">
                  <span
                    aria-hidden
                    className={cn('h-2 w-2 shrink-0 rounded-full',
                      row.source === 'label' ? 'bg-accent'
                        : row.source === 'alias' ? 'bg-accent-tint'
                          : row.source === 'model' ? 'bg-warn' : 'bg-line-strong')}
                  />
                  {{
                    label: 'exact', alias: 'known name', model: 'a guess', none: 'not matched',
                  }[row.source]}
                </span>

                <select
                  value={row.field ?? ''}
                  aria-label={`What "${row.column}" is`}
                  onChange={(event) => setColumns(columns.map((one) => (one.column === row.column
                    ? { ...one, field: event.target.value || null, source: 'label' as const }
                    : one)))}
                  className={cn('h-8 w-full rounded-field border border-line bg-surface px-2',
                    'text-body text-text focus:border-accent-line focus:outline-none')}
                >
                  <option value="">Ignore this column</option>
                  {(targets.length > 0 ? targets : fields).map((field) => (
                    <option key={field.name} value={field.name}>{field.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 'clean' && preview && (
        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <MetricCard label="Add" value={String(preview.counts.create ?? 0)} />
            <MetricCard label="Update" value={String(preview.counts.update ?? 0)} />
            <MetricCard label="No change" value={String(preview.counts.skip ?? 0)} />
            <MetricCard label="Problems" value={String(preview.counts.error ?? 0)} />
          </div>

          {problems.length > 0 && (
            <div className={cn('max-h-[180px] overflow-y-auto rounded-card border',
              'border-line bg-surface-alt p-2')}>
              {problems.slice(0, 30).map((row) => (
                <p key={row.rowNumber} className="m-0 text-[12px] text-text-muted">
                  Row {row.rowNumber}: {row.error}
                </p>
              ))}
              {problems.length > 30 && (
                <p className="m-0 text-[12px] text-text-faint">
                  …and {problems.length - 30} more.
                </p>
              )}
            </div>
          )}

          <p className="m-0 text-[12px] text-text-muted">
            Rows with problems are left alone. The rest are written exactly as counted
            above, and you can put the whole batch back afterwards.
          </p>
        </div>
      )}

      {step === 'done' && summary && (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-cell text-text">{summary.text}</p>
          <p className="m-0 text-[12px] text-text-muted">
            Undo restores what was changed and switches off what was added — it does not
            delete anything, so a row somebody has already quoted from stays put.
            Available for a week.
          </p>
        </div>
      )}

      {error && <p className="mb-0 mt-3 text-cell text-danger">{error}</p>}
    </Modal>
  )
}

/**
 * One CSV line into cells.
 *
 * Enough of the format to read a header row and a sample: quoted fields,
 * doubled quotes inside them, commas everywhere else. The server parses the
 * file properly — this only has to be right about what to *call* the columns.
 */
function splitRow(line: string): string[] {
  const cells: string[] = []
  let cell = ''
  let quoted = false

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    if (quoted) {
      if (char === '"' && line[index + 1] === '"') { cell += '"'; index += 1 }
      else if (char === '"') quoted = false
      else cell += char
    } else if (char === '"') quoted = true
    else if (char === ',') { cells.push(cell.trim()); cell = '' }
    else cell += char
  }
  cells.push(cell.trim())
  return cells
}
